/** Basic email shape check (non-empty local + domain with a TLD). */
export function isValidEmail(v: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.trim());
}

/**
 * Pulls email addresses out of a patient / clinical file record, tolerating the several
 * shapes the API uses:
 *   - a plain string:            "x@y.com"
 *   - an array of strings:       ["x@y.com"]
 *   - an array of { value }:     [{ value: "x@y.com" }]
 *   - an object map of entries:  { key: { value: "x@y.com" } }  (personalInfo.emails)
 *
 * Returns a de-duplicated list of trimmed, non-empty addresses.
 */
export function extractEmails(obj: any): string[] {
  if (!obj) return [];

  const sources = [
    obj.emails,
    obj.email,
    obj.contactEmail,
    obj.contact?.email,
    obj.personalInfo?.emails,
    obj.personalInfo?.email
  ];

  const out: string[] = [];
  const pushValue = (v: any) => {
    if (!v) return;
    if (typeof v === "string") out.push(v);
    else if (typeof v.value === "string") out.push(v.value);
    else if (typeof v.email === "string") out.push(v.email);
  };

  for (const src of sources) {
    if (!src) continue;
    if (typeof src === "string") out.push(src);
    else if (Array.isArray(src)) src.forEach(pushValue);
    else if (typeof src === "object") Object.values(src).forEach(pushValue);
  }

  return Array.from(new Set(out.map(e => e.trim()).filter(Boolean)));
}

/**
 * The first address, or `undefined` when the record carries none.
 *
 * `extractEmails(x)[0]` reads as a `string` to the compiler — indexed access is
 * not checked unless `noUncheckedIndexedAccess` is on — so callers that took it
 * at face value would hit an undefined at runtime. This states the truth.
 */
export function firstEmail(obj: any): string | undefined {
  return extractEmails(obj)[0];
}
